import { PLAN_KEYS } from "@giftforward/entitlements";
import { InfoOutlined, Visibility } from "@mui/icons-material";
import { Box, Button, Chip, IconButton, Tooltip } from "@mui/material";
import { type ReactNode, useState } from "react";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import DemoSeedDialog from "@/components/usersOrgs/DemoSeedDialog";
import OrgDetail from "@/components/usersOrgs/OrgDetail";
import { useDemoRows } from "@/hooks/useDemo";
import { useImpersonate } from "@/hooks/useImpersonate";
import { useOrganization } from "@/hooks/useOrganizations";
import { usePermissions } from "@/hooks/usePermissions";
import type { DemoRow } from "@/interfaces/demo";
import { formatDate } from "@/utils/date-format";

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Users & Orgs → Demo. One row per committed demo bundle (apps/gfgf-api/drizzle/json_data/
 * demo_accounts), joined with the `is_fake` organization it produced in THIS environment, if any.
 * Demo orgs are excluded from the Organizations tab, so this is where they live for GFG staff:
 * see the seeded state, (re)seed from the bundle, impersonate the demo login, open the org.
 *
 * The list is a handful of rows and comes back whole, so the blotter's query state is applied
 * client-side (search only; no filters, no server paging).
 */
export default function DemoBlotter() {
  const [seedRow, setSeedRow] = useState<DemoRow | null>(null);
  const [openOrgId, setOpenOrgId] = useState<number | null>(null);
  const { can } = usePermissions();
  const canSeed = can("seed", "DEMO");
  const canImpersonate = can("start", "IMPERSONATION");
  const impersonate = useImpersonate();
  const { data: openOrg } = useOrganization(openOrgId);

  if (openOrgId !== null && openOrg) {
    return <OrgDetail org={openOrg} onBack={() => setOpenOrgId(null)} />;
  }

  function handleImpersonate(row: DemoRow) {
    const clerkUserId = row.seeded?.loginClerkUserId;
    if (!clerkUserId) return;
    void impersonate.impersonate(clerkUserId);
  }

  const config: BlotterConfig<DemoRow> = {
    title: "Demo organizations",
    searchPlaceholder: "Search demo organizations",
    itemNoun: "demo org",
    minWidth: 900,
    rowKey: (r) => r.slug,
    exportFilename: "demo-organizations.csv",
    filterFields: [],
    useData: useDemoData,
    leadingCell: canImpersonate
      ? (r) => {
          if (!r.seeded) return null;
          if (!r.seeded.loginClerkUserId) {
            const reason =
              "The demo login has no Clerk account, so there is no session to impersonate.";
            return (
              <Tooltip title={reason}>
                <span
                  role="img"
                  aria-label={reason}
                  style={{ display: "inline-flex", cursor: "help" }}
                >
                  <InfoOutlined sx={{ fontSize: 18, color: "var(--color-graphite)" }} />
                </span>
              </Tooltip>
            );
          }
          return (
            <Tooltip title={`Impersonate ${r.loginEmail}`}>
              <span style={{ display: "inline-flex" }}>
                <IconButton
                  size="small"
                  aria-label={`Impersonate ${r.loginEmail}`}
                  disabled={impersonate.isPending}
                  onClick={() => handleImpersonate(r)}
                >
                  <Visibility sx={{ fontSize: 18, color: "var(--color-dark-dust)" }} />
                </IconButton>
              </span>
            </Tooltip>
          );
        }
      : undefined,
    columns: [
      {
        key: "orgName",
        label: "Organization",
        sortable: true,
        sortValue: (r) => r.orgName,
        render: (r) =>
          r.seeded ? (
            <button
              type="button"
              onClick={() => setOpenOrgId(r.seeded?.organizationId ?? null)}
              style={{
                border: "none",
                background: "none",
                padding: 0,
                cursor: "pointer",
                fontWeight: 600,
                fontFamily: "var(--font-lato)",
                fontSize: 14,
                color: "var(--color-rodeo-black)",
                textAlign: "left",
              }}
            >
              {r.orgName}
            </button>
          ) : (
            <span style={{ fontWeight: 600, color: "var(--color-graphite)" }}>{r.orgName}</span>
          ),
      },
      {
        key: "tier",
        label: "Tier",
        sortable: true,
        sortValue: (r) => SORT_VALUE.tier?.(r) ?? 0,
        render: (r) => titleCase(r.tier),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        sortValue: (r) => SORT_VALUE.status?.(r) ?? 0,
        exportValue: (r) =>
          r.seeding
            ? "Seeding"
            : r.seeded
              ? `${r.seeded.stale ? "Stale" : "Seeded"} ${formatDate(r.seeded.seededAt)}`
              : "Not seeded",
        render: (r) => {
          if (r.seeding) return <Chip size="small" variant="outlined" label="Seeding…" />;
          if (!r.seeded) {
            return (
              <Chip
                size="small"
                variant="outlined"
                label="Not seeded"
                sx={{ color: "var(--color-graphite)" }}
              />
            );
          }
          return (
            <Tooltip
              title={
                r.seeded.stale
                  ? `The committed bundle (${formatDate(r.extractedAt)}) is newer than this organization. Reseed to pick it up.`
                  : `Seeded ${formatDate(r.seeded.seededAt)} as organization #${r.seeded.organizationId}`
              }
            >
              <Chip
                size="small"
                variant="outlined"
                color={r.seeded.stale ? "warning" : "success"}
                label={`${r.seeded.stale ? "Stale" : "Seeded"} ${formatDate(r.seeded.seededAt)}`}
              />
            </Tooltip>
          );
        },
      },
      {
        key: "users",
        label: "Users",
        sortValue: (r) => r.seeded?.users ?? r.users,
        exportValue: (r) => String(r.seeded?.users ?? r.users),
        render: (r) => (r.seeded ? r.seeded.users : <Dim>{r.users}</Dim>),
      },
      {
        key: "campaigns",
        label: "Campaigns",
        sortValue: (r) => r.seeded?.campaigns ?? r.campaigns,
        exportValue: (r) => String(r.seeded?.campaigns ?? r.campaigns),
        render: (r) => (r.seeded ? r.seeded.campaigns : <Dim>{r.campaigns}</Dim>),
      },
      {
        key: "impact",
        label: "Impact report",
        sortValue: (r) => (r.hasImpactReport ? 1 : 0),
        exportValue: (r) => (r.hasImpactReport ? "Yes" : "No"),
        render: (r) =>
          r.hasImpactReport ? (
            "Generated"
          ) : (
            <Tooltip title="No <slug>.impact.json beside the bundle — the Impact page will be empty for this org.">
              <span style={{ color: "var(--color-graphite)", cursor: "help" }}>None</span>
            </Tooltip>
          ),
      },
      {
        key: "loginEmail",
        label: "Login",
        sortValue: (r) => r.loginEmail,
        render: (r) => <code style={{ fontSize: 12.5 }}>{r.loginEmail}</code>,
      },
      {
        key: "actions",
        label: "",
        exportValue: () => "",
        cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
        render: (r) => {
          if (!canSeed) {
            return (
              <Tooltip title="Only a GFG Super Admin can seed or reseed a demo organization.">
                <span style={{ fontSize: 12, color: "var(--color-graphite)", cursor: "help" }}>
                  Super admin only
                </span>
              </Tooltip>
            );
          }
          return (
            <Button
              size="small"
              variant={r.seeded ? "outlined" : "contained"}
              disableElevation
              disabled={r.seeding}
              onClick={() => setSeedRow(r)}
              sx={{
                fontFamily: "var(--font-lato)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                borderRadius: "6px",
                ...(r.seeded
                  ? {
                      color: "var(--color-rodeo-black)",
                      borderColor: "var(--color-smoke)",
                      "&:hover": {
                        borderColor: "var(--color-dark-dust)",
                        bgcolor: "var(--color-white)",
                      },
                    }
                  : {
                      bgcolor: "var(--color-rodeo-black)",
                      color: "var(--color-white)",
                      "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
                    }),
              }}
            >
              {r.seeding ? "Seeding…" : r.seeded ? "Reseed" : "Seed"}
            </Button>
          );
        },
      },
    ],
  };

  return (
    <Box>
      <Blotter config={config} />
      <DemoSeedDialog row={seedRow} onClose={() => setSeedRow(null)} />
    </Box>
  );
}

function Dim({ children }: { children: ReactNode }) {
  return (
    <Tooltip title="In the bundle; not seeded here yet.">
      <span style={{ color: "var(--color-graphite)" }}>{children}</span>
    </Tooltip>
  );
}

/** Sort payloads, by column key — the blotter expects its data hook to apply the sort. */
const SORT_VALUE: Record<string, (r: DemoRow) => string | number> = {
  orgName: (r) => r.orgName,
  tier: (r) => PLAN_KEYS.indexOf(r.tier),
  status: (r) => (r.seeding ? 3 : r.seeded ? (r.seeded.stale ? 2 : 1) : 0),
  users: (r) => r.seeded?.users ?? r.users,
  campaigns: (r) => r.seeded?.campaigns ?? r.campaigns,
  impact: (r) => (r.hasImpactReport ? 1 : 0),
  loginEmail: (r) => r.loginEmail,
};

/** Whole list from the API, searched and sorted client-side — a handful of rows, no paging. */
function useDemoData(q: {
  search: string;
  sortBy: string;
  sortDir: "asc" | "desc";
}): BlotterData<DemoRow> {
  const { data, isLoading } = useDemoRows();
  const needle = q.search.trim().toLowerCase();
  const sortValue = SORT_VALUE[q.sortBy] ?? SORT_VALUE.orgName;
  const dir = q.sortDir === "desc" ? -1 : 1;
  const rows = (data ?? [])
    .filter((r) => !needle || r.orgName.toLowerCase().includes(needle) || r.slug.includes(needle))
    .sort((a, b) => {
      const av = sortValue?.(a) ?? "";
      const bv = sortValue?.(b) ?? "";
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    });
  return { data: rows, total: rows.length, totalPages: 1, isLoading };
}
